import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Paper, Stack, TextField, Typography,
} from '@mui/material';
import AdminLocationStatusEditor from '../components/AdminLocationStatusEditor';
import {
  fetchUserPasswordDetails, fetchUserPasswordGenerationContext, generateUserPassword,
} from '../helpers/userPasswordsApi';
import { updateLocationStatus } from '../helpers/locationStatusApi';
import { getSelectedLocationStatusChanges, locationStatusKey } from '../helpers/adminLocationStatus';
import { areLocationScopesEqual, formatLocationScopeLabel } from '../helpers/locationScopes';
import { formatDateTimesInText, formatHumanDateTime, getDefaultFutureDateTimeInput } from '../helpers/dateTime';

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();
const passwordDetails = (result) => ({
  createdAt: result.createdAt || '', updatedAt: result.updatedAt || '', expiresAt: result.expiresAt || '',
});
const statusWarnings = (results) => results.flatMap((result) => {
  if (result.status === 'failed') return [`${formatLocationScopeLabel(result.locationScope)}: ${result.message || 'No se pudo guardar el estado de la sede.'}`];
  return result.notification?.status === 'failed' ? [result.notification.message] : [];
});

function UserPasswordsAdmin({
  accessToken, initialEmail = '', initialLocationLabel = '', embedded = false,
  onCompleted, onBusyChange,
}) {
  const [email, setEmail] = useState(initialEmail);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [accessEmailWarning, setAccessEmailWarning] = useState('');
  const [lastPassword, setLastPassword] = useState('');
  const [expiresAtInput, setExpiresAtInput] = useState(getDefaultFutureDateTimeInput);
  const [lastPasswordDetails, setLastPasswordDetails] = useState(null);
  const [passwordDisplayMode, setPasswordDisplayMode] = useState('');
  const [review, setReview] = useState(null);
  const [reviewError, setReviewError] = useState('');
  const [statusDrafts, setStatusDrafts] = useState({});
  const [pendingStatuses, setPendingStatuses] = useState([]);
  const lifecycle = useRef({ accessToken, email, initialEmail, mounted: true, generation: 0 });
  const callbacks = useRef({ onCompleted, onBusyChange });
  callbacks.current = { onCompleted, onBusyChange };
  if (lifecycle.current.accessToken !== accessToken || lifecycle.current.email !== email || lifecycle.current.initialEmail !== initialEmail) {
    lifecycle.current = { accessToken, email, initialEmail, mounted: true, generation: lifecycle.current.generation + 1 };
  }
  const busy = loadingPassword || preparing || generating || retrying;
  const canSubmit = Boolean(normalizeEmail(email) && expiresAtInput && accessToken && !busy);
  const expiresAtPreview = useMemo(() => formatHumanDateTime(expiresAtInput, 'Selecciona una fecha y hora'), [expiresAtInput]);
  const expiresAtFieldSx = {
    '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(255, 152, 0, 0.08)', borderRadius: 1.5 },
    '& .MuiInputLabel-root': { color: 'secondary.main', fontWeight: 700 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main', borderWidth: 1.5 },
    '& .MuiFormHelperText-root': { color: 'secondary.dark', fontWeight: 500, mx: 0.25 },
  };

  const isCurrent = useCallback((generation) => lifecycle.current.mounted && lifecycle.current.generation === generation, []);

  useEffect(() => {
    lifecycle.current.mounted = true;
    return () => {
      lifecycle.current.mounted = false;
      lifecycle.current.generation += 1;
      callbacks.current.onBusyChange?.(false);
    };
  }, []);

  useEffect(() => { callbacks.current.onBusyChange?.(busy); }, [busy]);

  useEffect(() => {
    setEmail(initialEmail || '');
    setExpiresAtInput(getDefaultFutureDateTimeInput());
  }, [initialEmail]);

  useEffect(() => {
    const generation = lifecycle.current.generation;
    setError('');
    setSuccess('');
    setWarnings([]);
    setAccessEmailWarning('');
    setLastPassword('');
    setLastPasswordDetails(null);
    setPasswordDisplayMode('');
    setReview(null);
    setReviewError('');
    setStatusDrafts({});
    setPendingStatuses([]);
    setPreparing(false);
    setGenerating(false);
    setRetrying(false);
    setLoadingPassword(false);
    const targetEmail = normalizeEmail(email);
    if (!accessToken || !targetEmail || targetEmail !== normalizeEmail(initialEmail)) return undefined;
    let cancelled = false;
    setLoadingPassword(true);
    fetchUserPasswordDetails(accessToken, targetEmail).then((result) => {
      if (cancelled || !isCurrent(generation)) return;
      if (result?.hasPassword) {
        setLastPassword(result.password || '');
        setLastPasswordDetails(passwordDetails(result));
        setPasswordDisplayMode('current');
      }
    }).catch((requestError) => {
      if (!cancelled && isCurrent(generation)) setError(requestError.message);
    }).finally(() => {
      if (!cancelled && isCurrent(generation)) setLoadingPassword(false);
    });
    return () => { cancelled = true; };
  }, [accessToken, email, initialEmail, isCurrent]);

  async function notifyCompleted(result, targetEmail) {
    // A dashboard refresh failure must not make a persisted credential look unsaved.
    try { await callbacks.current.onCompleted?.({ result, email: targetEmail }); } catch { /* The saved result remains visible. */ }
  }

  async function handleGenerate(event) {
    event.preventDefault();
    if (!canSubmit) return;
    await prepareReview('generate');
  }

  async function prepareReview(mode) {
    if (!normalizeEmail(email) || !accessToken || busy) return;
    const generation = lifecycle.current.generation;
    const targetEmail = normalizeEmail(email);
    setPreparing(true);
    setError('');
    setSuccess('');
    setWarnings([]);
    try {
      const context = await fetchUserPasswordGenerationContext(accessToken, targetEmail);
      if (!isCurrent(generation)) return;
      if (normalizeEmail(context.email || targetEmail) !== targetEmail) throw new Error('El correo consultado cambió. Vuelve a consultar las sedes del usuario.');
      setStatusDrafts({});
      setReviewError('');
      setReview({
        ...context, email: targetEmail, expiresAt: expiresAtInput, generation, mode,
        locationStatuses: Array.isArray(context.locationStatuses) ? context.locationStatuses : [],
      });
    } catch (requestError) {
      if (isCurrent(generation)) setError(requestError.message || 'No se pudieron consultar las sedes del usuario.');
    } finally {
      if (isCurrent(generation)) setPreparing(false);
    }
  }

  async function saveStatusReview() {
    if (!review || review.mode !== 'status_only' || busy || !isCurrent(review.generation) || review.email !== normalizeEmail(email)) return;
    const generation = review.generation;
    const changes = getSelectedLocationStatusChanges(review.locationStatuses.map((entry) => entry.locationScope), statusDrafts);
    if (!changes.length) return;
    setRetrying(true);
    setReviewError('');
    try {
      const current = await fetchUserPasswordGenerationContext(accessToken, review.email);
      if (!isCurrent(generation)) return;
      for (const change of changes) {
        const assigned = current.locationStatuses.find((entry) => areLocationScopesEqual(entry.locationScope, change.locationScope));
        if (!assigned) throw new Error('Las sedes asignadas cambiaron. Cierra esta ventana y vuelve a consultar las sedes del usuario.');
        if (change.status === 'up_to_date' && assigned.pendingRequestsCount > 0) throw new Error('Hay solicitudes pendientes. No se puede marcar Todo está al día.');
      }
      const results = [];
      for (const change of changes) {
        if (!isCurrent(generation)) return;
        try {
          const result = await updateLocationStatus(change.locationScope, change.status, accessToken);
          results.push({ locationScope: change.locationScope, requestedStatus: change.status, status: 'saved', notification: result.notification, record: result.status });
        } catch (requestError) {
          results.push({ locationScope: change.locationScope, requestedStatus: change.status, status: 'failed', message: requestError.message });
        }
      }
      if (!isCurrent(generation)) return;
      setReview(null);
      setStatusDrafts({});
      setPendingStatuses(results.filter((entry) => entry.status === 'failed'));
      setWarnings(statusWarnings(results));
      const savedCount = results.filter((entry) => entry.status === 'saved').length;
      setSuccess(savedCount ? `Se guardó el estado de ${savedCount} sede(s). La contraseña del usuario se mantiene sin cambios.` : '');
      await notifyCompleted({ locationStatusResults: results, locationStatusesUpdated: savedCount > 0 }, review.email);
    } catch (requestError) {
      if (isCurrent(generation)) setReviewError(requestError.message || 'No se pudieron guardar los estados.');
    } finally {
      if (isCurrent(generation)) setRetrying(false);
    }
  }

  async function confirmGeneration() {
    if (!review || review.mode !== 'generate' || busy || !isCurrent(review.generation) || review.email !== normalizeEmail(email)) return;
    const generation = review.generation;
    const targetEmail = review.email;
    const changes = getSelectedLocationStatusChanges(review.locationStatuses.map((entry) => entry.locationScope), statusDrafts);
    setGenerating(true);
    setReviewError('');
    try {
      const result = await generateUserPassword(
        accessToken, targetEmail, Boolean(review.exists || review.hasPassword),
        typeof window === 'undefined' ? '' : window.location.origin,
        review.expiresAt,
        review.locationStatuses.map((entry) => formatLocationScopeLabel(entry.locationScope)).join('; ')
          || (targetEmail === normalizeEmail(initialEmail) ? String(initialLocationLabel || '').trim() : ''),
        changes,
      );
      if (!isCurrent(generation)) return;
      if (result.exists && !result.created && !result.replaced) {
        // Another administrator may have generated access after the read-only review.
        setReview((previous) => ({ ...previous, exists: true, hasPassword: true }));
        setLastPassword(result.password || '');
        setLastPasswordDetails(passwordDetails(result));
        setPasswordDisplayMode('current');
        setReviewError('Ya existe una contraseña. Revisa las opciones y confirma si deseas reemplazarla.');
        return;
      }
      setLastPassword(result.password || '');
      setLastPasswordDetails(passwordDetails(result));
      setPasswordDisplayMode('generated');
      setReview(null);
      setStatusDrafts({});
      const results = Array.isArray(result.locationStatusResults) ? result.locationStatusResults : [];
      const messages = statusWarnings(results);
      setAccessEmailWarning(result.emailSent === false ? result.emailError || 'El acceso se guardó, pero no se pudo enviar por correo.' : '');
      setWarnings(messages);
      setPendingStatuses(results.filter((entry) => entry.status === 'failed'));
      setSuccess(result.replaced
        ? `La contraseña existente fue reemplazada${result.emailSent ? ' y enviada por correo' : ''} correctamente.`
        : `Contraseña generada y guardada${result.emailSent ? ', y enviada por correo' : ''} correctamente.`);
      await notifyCompleted(result, targetEmail);
    } catch (requestError) {
      if (isCurrent(generation)) setReviewError(requestError.message || 'No se pudo generar el acceso.');
    } finally {
      if (isCurrent(generation)) setGenerating(false);
    }
  }

  async function retryStatuses() {
    if (!pendingStatuses.length || busy) return;
    const generation = lifecycle.current.generation;
    const targetEmail = normalizeEmail(email);
    setRetrying(true);
    try {
      const context = await fetchUserPasswordGenerationContext(accessToken, targetEmail);
      if (!isCurrent(generation)) return;
      const results = [];
      for (const entry of pendingStatuses) {
        if (!isCurrent(generation)) return;
        const assigned = context.locationStatuses.some((current) => areLocationScopesEqual(current.locationScope, entry.locationScope));
        if (!assigned) {
          results.push({ ...entry, status: 'skipped', message: 'La sede ya no está asignada a este usuario. Vuelve a revisar sus sedes antes de cambiarla.' });
          continue;
        }
        try {
          const result = await updateLocationStatus(entry.locationScope, entry.requestedStatus, accessToken);
          results.push({ ...entry, status: 'saved', notification: result.notification });
        } catch (requestError) {
          results.push({ ...entry, status: 'failed', message: requestError.message });
        }
      }
      if (!isCurrent(generation)) return;
      setPendingStatuses(results.filter((entry) => entry.status === 'failed'));
      setWarnings([...statusWarnings(results), ...results.filter((entry) => entry.status === 'skipped').map((entry) => entry.message)]);
      if (results.every((entry) => entry.status === 'saved')) setSuccess('Los estados pendientes se guardaron. El acceso del usuario se mantiene sin cambios.');
      await notifyCompleted({ locationStatusResults: results }, targetEmail);
    } catch (requestError) {
      if (isCurrent(generation)) setWarnings([`No se pudieron reintentar los estados: ${requestError.message}`]);
    } finally {
      if (isCurrent(generation)) setRetrying(false);
    }
  }

  const content = (
    <Stack spacing={1.5} component="form" onSubmit={handleGenerate}>
      {!embedded && <Typography variant="h6">Gestión de contraseñas de usuarios</Typography>}
      <Typography color="text.secondary">
        Puedes generar o reemplazar el acceso temporal del usuario y enviarlo por correo. Antes de generarlo, podrás confirmar si deseas mantener o cambiar el estado de sus sedes.
      </Typography>
      <TextField label="Correo del usuario" type="email" value={email} onChange={(event) => setEmail(event.target.value)} disabled={busy} fullWidth />
      <TextField
        label="Expira el" type="datetime-local" value={expiresAtInput} onChange={(event) => setExpiresAtInput(event.target.value)}
        disabled={busy} fullWidth color="secondary" sx={expiresAtFieldSx} InputLabelProps={{ shrink: true }}
        helperText={`Se mostrará como: ${expiresAtPreview}`}
      />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button type="submit" variant="contained" disabled={!canSubmit} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
          {preparing ? 'Consultando sedes…' : generating ? 'Generando…' : 'Generar contraseña'}
        </Button>
        <Button type="button" variant="outlined" onClick={() => prepareReview('status_only')} disabled={!normalizeEmail(email) || !accessToken || busy}>
          Gestionar estado de las sedes
        </Button>
      </Stack>
      {lastPassword && (
        <Stack spacing={1}>
          <TextField label={passwordDisplayMode === 'current' ? 'Contraseña actual' : 'Última contraseña generada'} value={lastPassword} InputProps={{ readOnly: true }} fullWidth helperText="Cópiala manualmente desde este campo." />
          {lastPasswordDetails && (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={0.75}>
                <Typography variant="subtitle2">{passwordDisplayMode === 'current' ? 'Vigencia actual' : 'Detalles de vigencia'}</Typography>
                <Typography color="text.secondary">Creada: {formatHumanDateTime(lastPasswordDetails.createdAt, lastPasswordDetails.createdAt || '-')}</Typography>
                <Typography color="text.secondary">Actualizada: {formatHumanDateTime(lastPasswordDetails.updatedAt, lastPasswordDetails.updatedAt || '-')}</Typography>
                <Typography color="text.secondary">Expira: {formatHumanDateTime(lastPasswordDetails.expiresAt, lastPasswordDetails.expiresAt || '-')}</Typography>
              </Stack>
            </Paper>
          )}
        </Stack>
      )}
      {error && <Alert severity="error">{formatDateTimesInText(error)}</Alert>}
      {success && <Alert severity="success">{formatDateTimesInText(success)}</Alert>}
      {accessEmailWarning && <Alert severity="warning">{formatDateTimesInText(accessEmailWarning)}</Alert>}
      {warnings.map((message, index) => <Alert key={index} severity="warning">{formatDateTimesInText(message)}</Alert>)}
      {pendingStatuses.length > 0 && <Button type="button" onClick={retryStatuses} disabled={busy}>Reintentar solo los estados pendientes</Button>}
    </Stack>
  );

  return (
    <Box component="main" sx={{ width: '100%', ...(embedded ? {} : { p: { xs: 1.5, md: 2 } }) }}>
      {embedded ? content : <Paper variant="outlined" sx={{ p: 2, maxWidth: 760 }}>{content}</Paper>}
      <Dialog open={Boolean(review)} onClose={() => { if (!busy) setReview(null); }} maxWidth="md" fullWidth>
        <DialogTitle>{review?.mode === 'status_only' ? 'Gestionar estado de las sedes' : review?.exists || review?.hasPassword ? 'Confirmar reemplazo del acceso' : 'Confirmar generación del acceso'}</DialogTitle>
        <DialogContent dividers>
          {review && (
            <Stack spacing={1.5}>
              <Typography>Usuario: <strong>{review.email}</strong></Typography>
              {review.mode === 'status_only' ? <Alert severity="info">Aquí solo se modifica el estado de las sedes. La contraseña del usuario se mantiene sin cambios.</Alert> : (
                <>
                  {(review.exists || review.hasPassword) && <Alert severity="warning">Este usuario ya tiene acceso. Al confirmar, su contraseña actual será reemplazada y su acceso anterior dejará de funcionar. Se enviará el nuevo acceso por correo.</Alert>}
                  <Typography color="text.secondary">El acceso expirará el {formatHumanDateTime(review.expiresAt, review.expiresAt)}.</Typography>
                </>
              )}
              <AdminLocationStatusEditor
                locations={review.locationStatuses.map((entry) => entry.locationScope)} statuses={review.locationStatuses}
                drafts={statusDrafts} onChange={(scope, status) => setStatusDrafts((previous) => ({ ...previous, [locationStatusKey(scope)]: status }))}
                disabled={busy}
                description={review.mode === 'status_only'
                  ? 'Elige si deseas mantener o cambiar el estado de cada sede. El estado elegido se aplicará a todos los usuarios de esa sede al pulsar Guardar estados de las sedes.'
                  : 'Elige si deseas mantener o cambiar el estado de cada sede. El estado elegido se aplicará a todos los usuarios de esa sede al confirmar la generación del acceso.'}
              />
              {!review.locationStatuses.length && <Alert severity="info">Este usuario no tiene sedes asignadas.{review.mode === 'status_only' ? '' : ' Se generará el acceso sin modificar estados.'}</Alert>}
              {reviewError && <Alert severity="error">{formatDateTimesInText(reviewError)}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setReview(null)} disabled={busy}>Cancelar</Button>
          <Button type="button" variant="contained" onClick={review?.mode === 'status_only' ? saveStatusReview : confirmGeneration}
            disabled={busy || (review?.mode === 'status_only' && !getSelectedLocationStatusChanges(review.locationStatuses.map((entry) => entry.locationScope), statusDrafts).length)}>
            {review?.mode === 'status_only' ? retrying ? 'Guardando estados…' : 'Guardar estados de las sedes' : generating ? 'Generando…' : review?.exists || review?.hasPassword ? 'Confirmar y reemplazar' : 'Confirmar y generar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserPasswordsAdmin;
